import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, walletApi, settingsApi } from './services/api';

const AppContext = createContext(null);

function resolveClientId(userData) {
  if (!userData) return '';
  return String(userData.ClientId || userData.clientid || userData.client_id || '');
}

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [clientId, setClientId] = useState(() => {
    try { return resolveClientId(JSON.parse(localStorage.getItem('user') || '{}')); } catch { return ''; }
  });
  const [walletBalance, setWalletBalance] = useState(0);
  const [holdBalance, setHoldBalance] = useState(0);
  const [totalBalance, setTotalBalance] = useState(0);
  const [minimumBalance, setMinimumBalance] = useState(0);
  const [invitationExpireDay, setInvitationExpireDay] = useState(0);
  const [notifications, setNotifications] = useState(3);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try {
        const u = JSON.parse(savedUser);
        setUser(u);
        setClientId(resolveClientId(u));
      } catch { localStorage.removeItem('user'); }
    }
    setLoading(false);
  }, []);

  const refreshWalletBalance = useCallback(async () => {
    try {
      const res = await walletApi.getWalletDetails();
      const record = Array.isArray(res) ? res[0] : res;
      if (!record) return;
      const cur   = parseFloat(record.current_balance);
      const hold  = parseFloat(record.hold_balance  ?? record.holdbalance  ?? 0);
      const total = parseFloat(record.total_balance  ?? record.totalbalance ?? 0);
      const minimum_balance = parseFloat(record.minimum_balance ?? 0);
      if (!isNaN(cur))   setWalletBalance(cur);
      if (!isNaN(hold))  setHoldBalance(hold);
      if (!isNaN(total)) setTotalBalance(total);
      setMinimumBalance(minimum_balance);
    } catch {}
  }, []);

  useEffect(() => {
    if (user) refreshWalletBalance();
  }, [user, refreshWalletBalance]);

  useEffect(() => {
    const handle = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('page');
      setUser(null);
      setClientId('');
    };
    window.addEventListener('auth:unauthorized', handle);
    return () => window.removeEventListener('auth:unauthorized', handle);
  }, []);

  function addToast(msg, type = 'default') {
    const id = Date.now();
    setToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  }

  useEffect(() => {
    if (!user) return;
    walletApi.getWalletDetails()
      .then(res => {
        const record = Array.isArray(res) ? res[0] : res;
        if (!record) return;
        setWalletBalance(parseFloat(record.current_balance)  || 0);
        setHoldBalance(parseFloat(record.hold_balance)       || 0);
        setTotalBalance(parseFloat(record.total_balance)     || 0);
        setMinimumBalance(parseFloat(record.minimum_balance) || 0);
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    settingsApi.getClientSettings()
      .then(res => {
        const d = res?.data;
        if (!d) return;
        if (d.invitation_expire_day != null && d.invitation_expire_day >= 0) {
          setInvitationExpireDay(parseFloat(d.invitation_expire_day));
        }
      })
      .catch(() => {});
  }, [user]);

  function login(userData, token, rawClientId) {
    const cid = rawClientId ? String(rawClientId) : resolveClientId(userData);
    const userWithClientId = { ...userData, ClientId: cid };
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userWithClientId));
    setUser(userWithClientId);
    setClientId(cid);
  }

  async function logout() {
    try { await authApi.logout(); } catch {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setClientId('');
  }

  return (
    <AppContext.Provider value={{ user, clientId, login, logout, loading, walletBalance, setWalletBalance, holdBalance, setHoldBalance, totalBalance, setTotalBalance, notifications, addToast, refreshWalletBalance, minimumBalance, setMinimumBalance, invitationExpireDay, setInvitationExpireDay }}>
      {children}
      <div className="toast-container">
        {toasts.map(t => (
          <div key={t.id} className={`toast ${t.type}`}>{t.msg}</div>
        ))}
      </div>
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
